import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ADVERTISEMENT_TYPES = {
  GENERAL: 1,
  WORKSHOP: 2,
  PRODUCT: 3
};

const BannerAdsFormModal = ({ isOpen, onClose, onSave, bannerAd, mode, language }) => {
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    image: null,
    advertisement_type: ADVERTISEMENT_TYPES.GENERAL
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (bannerAd) {
      setFormData({
        title: bannerAd?.title || '',
        body: bannerAd?.body || '',
        image: null,
        advertisement_type:
          bannerAd?.advertisement_type || ADVERTISEMENT_TYPES.GENERAL
      });
      setImagePreview(bannerAd?.image || '');
    } else {
      setFormData({
        title: '',
        body: '',
        image: null,
        advertisement_type: ADVERTISEMENT_TYPES.GENERAL
      });
      setImagePreview('');
    }
    setErrors({});
  }, [bannerAd, isOpen]);

  const labels = {
    add: { en: 'Add Banner Ad', ar: 'إضافة إعلان' },
    edit: { en: 'Edit Banner Ad', ar: 'تعديل الإعلان' },
    title: { en: 'Title', ar: 'العنوان' },
    body: { en: 'Body', ar: 'الوصف' },
    image: { en: 'Image', ar: 'الصورة' },
    type: { en: 'Advertisement Type', ar: 'نوع الإعلان' },
    general: { en: 'General', ar: 'عام' },
    workshop: { en: 'Workshop', ar: 'ورشة' },
    product: { en: 'Product', ar: 'منتج' },
    save: { en: 'Save', ar: 'حفظ' },
    cancel: { en: 'Cancel', ar: 'إلغاء' },
    required: { en: 'Required field', ar: 'حقل مطلوب' }
  };

  const advertisementTypesOptions = [
    {
      value: ADVERTISEMENT_TYPES.GENERAL,
      label: labels.general[language]
    },
    {
      value: ADVERTISEMENT_TYPES.WORKSHOP,
      label: labels.workshop[language]
    },
    {
      value: ADVERTISEMENT_TYPES.PRODUCT,
      label: labels.product[language]
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = labels.required[language];
    if (!formData.body.trim()) newErrors.body = labels.required[language];
    if (!imagePreview && !formData.image)
      newErrors.image = labels.required[language];
    if (!formData.advertisement_type)
      newErrors.advertisement_type = labels.required[language];

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    onSave({
      title: formData.title,
      body: formData.body,
      advertisement_type: formData.advertisement_type,
      image: formData.image || imagePreview
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, image: '' }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-card w-full max-w-xl rounded-xl p-6">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-bold">
            {mode === 'add' ? labels.add[language] : labels.edit[language]}
          </h2>
          <button onClick={onClose}>
            <Icon name="X" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={labels.title[language]}
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            error={errors.title}
          />

          <Input
            label={labels.body[language]}
            value={formData.body}
            onChange={(e) =>
              setFormData({ ...formData, body: e.target.value })
            }
            error={errors.body}
          />

          {/* Advertisement Type using Select component */}
          <Select
            label={labels.type[language]}
            options={advertisementTypesOptions}
            value={formData.advertisement_type}
            onChange={(value) =>
              setFormData({
                ...formData,
                advertisement_type: value
              })
            }
            error={errors.advertisement_type}
          />

          {/* Image Upload */}
          <div>
            <label className="text-sm font-medium">
              {labels.image[language]}
            </label>

            {imagePreview && (
              <img
                src={imagePreview}
                alt="preview"
                className="h-40 w-full object-cover rounded mb-2"
              />
            )}

            <input type="file" accept="image/*" onChange={handleImageUpload} />

            {errors.image && (
              <p className="text-sm text-error">{errors.image}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              {labels.cancel[language]}
            </Button>
            <Button type="submit" fullWidth>
              {labels.save[language]}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BannerAdsFormModal;